import { useEffect, useState } from "react";
import { Outlet, useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import Avatar from "@mui/material/Avatar";
import Drawer from "@mui/material/Drawer";
import IconButton from "@mui/material/IconButton";
import Divider from "@mui/material/Divider";
import Stack from "@mui/material/Stack";
import Tooltip from "@mui/material/Tooltip";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import HomeOutlinedIcon from "@mui/icons-material/HomeOutlined";
import AssignmentTurnedInOutlinedIcon from "@mui/icons-material/AssignmentTurnedInOutlined";
import LibraryBooksOutlinedIcon from "@mui/icons-material/LibraryBooksOutlined";
import PlaylistAddCheckOutlinedIcon from "@mui/icons-material/PlaylistAddCheckOutlined";
import TravelExploreOutlinedIcon from "@mui/icons-material/TravelExploreOutlined";
import PeopleOutlinedIcon from "@mui/icons-material/PeopleOutlined";
import LightbulbOutlinedIcon from "@mui/icons-material/LightbulbOutlined";
import FactCheckOutlinedIcon from "@mui/icons-material/FactCheckOutlined";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import LogoutOutlinedIcon from "@mui/icons-material/LogoutOutlined";
import MenuIcon from "@mui/icons-material/Menu";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useTheme } from "@mui/material/styles";
import { NavItem } from "../../../components/primitives/navigation/NavItem";
import { SectionLabel } from "../../../components/primitives/data-display/SectionLabel";
import { KODAKKU_SIDEBAR_BG } from "../../../theme.kodakku";
import { RequireRole } from "../../../components/gate/RequireRole";
import { useUserContext } from "../../../context/UserContext";
import { useInternalAuth } from "./useInternalAuth";
import { useInternalLayout } from "./useInternalLayout";
import { ROUTES } from "../../../route_paths";

const SIDEBAR_WIDTH = 196;
const SIDEBAR_WIDTH_COLLAPSED = 56;

const NAV_SECTIONS = [
  { to: ROUTES.internal.home.path(), label: "Home", icon: <HomeOutlinedIcon /> },
  {
    to: ROUTES.internal.annotation.path(),
    label: "Annotation",
    icon: <AssignmentTurnedInOutlinedIcon />,
  },
  { to: ROUTES.internal.library.path(), label: "Library", icon: <LibraryBooksOutlinedIcon /> },
  {
    to: ROUTES.internal.batchJobs.path(),
    label: "Batch jobs",
    icon: <PlaylistAddCheckOutlinedIcon />,
  },
  {
    to: ROUTES.internal.investigations.path(),
    label: "Investigations",
    icon: <TravelExploreOutlinedIcon />,
  },
] as const;

const ADMIN_NAV_SECTIONS = [
  { to: ROUTES.internal.admin.users.path(), label: "Users", icon: <PeopleOutlinedIcon /> },
] as const;

/** Reserves the brand-mark slot: renders /assets/kodakku-mark.webp, falls back to letter. */
function KodakkuMark() {
  return (
    <Avatar
      src="/assets/kodakku-mark.webp"
      variant="circular"
      sx={{ width: 44, height: 44, color: "primary.contrastText" }}
    >
      K
    </Avatar>
  );
}

/**
 * Bottom-pinned drawer footer: links back out of Kodakku (Expert Insights — role-gated — and the
 * main verify app) plus account access, since Kodakku hides the top NavBar those used to live in.
 */
function SidebarFooter({ collapsed, onNavigate }: { collapsed: boolean; onNavigate?: () => void }) {
  const { t } = useTranslation();
  const { signOut } = useUserContext();
  const navigate = useNavigate();

  async function handleSignOut() {
    onNavigate?.();
    await signOut();
    navigate(ROUTES.landing.path(), { replace: true });
  }

  return (
    <Stack gap={0.5}>
      <Divider sx={{ my: 0.5 }} />
      <RequireRole role="expert_insights.expert">
        <NavItem
          to={ROUTES.expertInsights.insights.path()}
          label={t("nav.expertInsights")}
          icon={<LightbulbOutlinedIcon />}
          collapsed={collapsed}
          onClick={onNavigate}
        />
      </RequireRole>
      <NavItem
        to={ROUTES.landing.path()}
        matchNested={false}
        label={t("header.verifyClaim")}
        icon={<FactCheckOutlinedIcon />}
        collapsed={collapsed}
        onClick={onNavigate}
      />
      <NavItem
        to={ROUTES.account.path()}
        matchNested={false}
        label={t("nav.myAccount")}
        icon={<PersonOutlineIcon />}
        collapsed={collapsed}
        onClick={onNavigate}
      />
      <NavItem
        label={t("nav.signOut")}
        icon={<LogoutOutlinedIcon />}
        collapsed={collapsed}
        onSelect={() => void handleSignOut()}
      />
    </Stack>
  );
}

/** Shared nav item list used in both the desktop sidebar and the mobile drawer. */
function NavItems({ collapsed, onNavigate }: { collapsed: boolean; onNavigate?: () => void }) {
  return (
    <Stack gap={0.5} sx={{ flex: 1 }}>
      {NAV_SECTIONS.map((section) => (
        <NavItem key={section.to} {...section} collapsed={collapsed} onClick={onNavigate} />
      ))}
      <RequireRole role="platform.admin">
        <>
          <Divider sx={{ my: 0.5 }} />
          {!collapsed && <SectionLabel sx={{ m: 0, px: 0.5 }}>Admin</SectionLabel>}
          {ADMIN_NAV_SECTIONS.map((section) => (
            <NavItem key={section.to} {...section} collapsed={collapsed} onClick={onNavigate} />
          ))}
        </>
      </RequireRole>
    </Stack>
  );
}

/**
 * Shell for all internal routes: RealityCheq's human-ops console — a collapsible left sidebar
 * (Slack model) whose footer links back out to the other sections (Expert Insights, the verify
 * app) and account, since Kodakku deliberately hides the shared top NavBar to reclaim vertical
 * space. On mobile the sidebar becomes a Drawer opened from a strip at the top. Includes the auth
 * guard via useInternalAuth.
 */
export default function InternalLayout() {
  const { session } = useInternalAuth();
  const { collapsed, toggleCollapsed } = useInternalLayout();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const [drawerOpen, setDrawerOpen] = useState(false);

  // Document-workspace routes (annotation review / improve ranking) need the horizontal space, so
  // the sidebar starts collapsed there regardless of the saved preference. The annotator can still
  // expand it while on the route (a per-visit override that never touches the persisted setting),
  // and their real preference resumes on any other route.
  const focusMode = /^\/internal\/annotation\/(review|improve-ranking)/.test(location.pathname);
  const [focusExpandOverride, setFocusExpandOverride] = useState(false);
  useEffect(() => {
    if (focusMode) setFocusExpandOverride(false);
  }, [focusMode]);
  const sidebarCollapsed = focusMode ? !focusExpandOverride : collapsed;
  const handleToggleSidebar = focusMode
    ? () => setFocusExpandOverride((prev) => !prev)
    : toggleCollapsed;

  if (!session) return null;

  return (
    <Stack direction="column" sx={{ height: "100vh", overflow: "hidden" }}>
      {/* Mobile Kodakku nav strip — hamburger to open the sidebar as a Drawer */}
      {isMobile && (
        <Stack
          direction="row"
          sx={{
            bgcolor: KODAKKU_SIDEBAR_BG,
            px: 1,
            py: 0.5,
            borderBottom: "1px solid",
            borderColor: "divider",
            alignItems: "center",
            gap: 1,
          }}
        >
          <IconButton
            size="small"
            onClick={() => setDrawerOpen(true)}
            aria-label="Open Kodakku navigation"
          >
            <MenuIcon />
          </IconButton>
          <SectionLabel sx={{ m: 0 }}>Kodakku</SectionLabel>
        </Stack>
      )}

      {/* Mobile sidebar drawer */}
      <Drawer anchor="left" open={drawerOpen} onClose={() => setDrawerOpen(false)}>
        <Stack sx={{ width: SIDEBAR_WIDTH, p: 1, gap: 0.75, height: "100%" }}>
          <Stack direction="row" gap={1} sx={{ alignItems: "center", px: 0.5, py: 0.5 }}>
            <KodakkuMark />
            <SectionLabel sx={{ m: 0 }}>Kodakku</SectionLabel>
          </Stack>
          <Divider />
          <NavItems collapsed={false} onNavigate={() => setDrawerOpen(false)} />
          <SidebarFooter collapsed={false} onNavigate={() => setDrawerOpen(false)} />
        </Stack>
      </Drawer>

      <Stack direction="row" sx={{ flex: 1, minHeight: 0, overflow: "hidden" }}>
        {/* Desktop sidebar */}
        <Stack
          component="nav"
          sx={{
            display: { xs: "none", md: "flex" },
            width: sidebarCollapsed ? SIDEBAR_WIDTH_COLLAPSED : SIDEBAR_WIDTH,
            flexShrink: 0,
            bgcolor: KODAKKU_SIDEBAR_BG,
            borderRight: "1px solid",
            borderColor: "divider",
            p: 1,
            gap: 0.75,
            transition: "width 0.15s ease",
          }}
        >
          <Stack
            direction="row"
            gap={1}
            sx={{
              alignItems: "center",
              justifyContent: sidebarCollapsed ? "center" : "space-between",
              px: 0.5,
            }}
          >
            <Stack direction="row" gap={1} sx={{ alignItems: "center" }}>
              <KodakkuMark />
              {!sidebarCollapsed && <SectionLabel sx={{ m: 0 }}>Kodakku</SectionLabel>}
            </Stack>
            {!sidebarCollapsed && (
              <Tooltip title="Collapse sidebar" placement="right">
                <IconButton size="small" onClick={handleToggleSidebar} aria-label="Toggle sidebar">
                  <ChevronLeftIcon />
                </IconButton>
              </Tooltip>
            )}
          </Stack>

          {sidebarCollapsed && (
            <Tooltip title="Expand sidebar" placement="right">
              <IconButton
                size="small"
                onClick={handleToggleSidebar}
                aria-label="Toggle sidebar"
                sx={{ alignSelf: "center" }}
              >
                <ChevronRightIcon />
              </IconButton>
            </Tooltip>
          )}

          <Divider />

          <NavItems collapsed={sidebarCollapsed} />
          <SidebarFooter collapsed={sidebarCollapsed} />
        </Stack>

        <Stack sx={{ flex: 1, minWidth: 0, overflowY: "auto" }}>
          <Outlet />
        </Stack>
      </Stack>
    </Stack>
  );
}
